import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal } from "@/components/Reveal";
import { getPath, getArticle } from "@/data/education";

export default function LearningPathPage() {
  const { pathSlug } = useParams();
  const path = getPath(pathSlug);
  if (!path) return <Navigate to="/education" replace />;

  const steps = path.articles.map(getArticle).filter(Boolean);

  return (
    <>
      <SEO
        title={path.title}
        description={path.summary}
        path={`/education/paths/${path.slug}`}
      />
      <PageHero
        overline={`Learning Path · ${path.level}`}
        titleLines={[path.title]}
        subtitle={path.summary}
        crumbs={[
          { label: "Education Center", path: "/education" },
          { label: path.title, path: `/education/paths/${path.slug}` },
        ]}
      />

      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center justify-between">
              <p className="overline text-electric">{steps.length}-step path</p>
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slatey">
                {path.level}
              </span>
            </div>

            <div className="mt-10 space-y-5">
              {steps.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.05}>
                  <Link
                    to={`/education/${a.slug}`}
                    data-testid={`path-step-${a.slug}`}
                    className="group flex items-start gap-6 border border-line p-6 transition-colors hover:border-electric md:p-8"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-electric font-mono text-sm text-electric">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.14em] text-slatey">
                        {a.categoryLabel}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {a.readingTime}
                        </span>
                      </div>
                      <h3 className="mt-2 font-heading text-xl font-medium text-navy transition-colors group-hover:text-electric">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slatey">{a.excerpt}</p>
                    </div>
                    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-slatey transition-colors group-hover:text-electric" />
                  </Link>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 border-t border-line pt-8">
              <Link
                to="/education"
                className="group inline-flex items-center gap-2 text-sm font-medium text-navy"
              >
                <ArrowRight className="h-4 w-4 rotate-180" />
                All learning paths & articles
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
